
import React, { createContext, useContext, useState } from 'react';
import { AIAssistantContextType } from './types';
import { useChatMessages } from './useChatMessages';
import { useTitleGenerator } from './useTitleGenerator';
import { useLeadGenerator } from './useLeadGenerator';
import { useTagGenerator } from './useTagGenerator';

const AIAssistantContext = createContext<AIAssistantContextType | undefined>(undefined);

export const useAIAssistant = () => {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within an AIAssistantProvider');
  }
  return context;
};

export const AIAssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { 
    messages, 
    sendMessage, 
    clearMessages, 
    rateResponse,
    verbosityLevel,
    setVerbosityLevel
  } = useChatMessages();

  const titleGenerator = useTitleGenerator();
  const leadGenerator = useLeadGenerator();
  const tagGenerator = useTagGenerator();

  // Combine the processing state and active feature from all generators
  const isProcessing = titleGenerator.isProcessing || leadGenerator.isProcessing || tagGenerator.isProcessing;
  const activeFeature = titleGenerator.activeFeature || leadGenerator.activeFeature || tagGenerator.activeFeature;

  const clearError = () => setError(null);

  return (
    <AIAssistantContext.Provider 
      value={{ 
        isOpen, 
        messages, 
        setIsOpen, 
        sendMessage, 
        clearMessages, 
        rateResponse,
        verbosityLevel,
        setVerbosityLevel,
        isProcessing,
        activeFeature,
        error,
        setError,
        clearError,
        generateTitles: titleGenerator.generateTitles,
        generateLeadParagraph: leadGenerator.generateLeadParagraph,
        generateTags: tagGenerator.generateTags
      }}
    >
      {children}
    </AIAssistantContext.Provider>
  );
};

// Also export with lowercase "ai" for backwards compatibility during transition
export { useAIAssistant as useAiAssistant };
export { AIAssistantProvider as AiAssistantProvider };
